import { createFileRoute, getRouteApi, useNavigate, redirect } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { wabaApi } from "../../lib/wabaApi";
import { ConnectionCard } from "../../components/waba/ConnectionCard";
import { WabaMetricsTab } from "../../components/waba/MetricsTab";
import { WabaMarketingTab } from "../../components/waba/MarketingTab";
import { WabaErrorsTab } from "../../components/waba/ErrorsTab";
import type { CurrentUser } from "../../lib/auth";
import { DialogPanel } from "../../components/shared/DialogPanel";
import { PhoneIcon, BookOpenIcon, XIcon } from "../../components/shared/Icons";
import { backdropClose } from "../../components/shared/backdrop";
const _authedRoute = getRouteApi("/_authed");

export const Route = createFileRoute("/_authed/waba")({
  beforeLoad: ({ context }) => {
    const u = (context as { user?: CurrentUser }).user;
    if (u && u.role_name !== "Admin" && u.permissions?.viewer) {
      throw redirect({ to: "/" });
    }
  },
  component: WabaPage,
  validateSearch: (s: Record<string, unknown>): { open?: string; log?: string; tab?: "metricas" | "marketing" | "erros" } => ({
    open: typeof s.open === "string" ? s.open : undefined,
    log: typeof s.log === "string" ? s.log : undefined,  // canal do Log de Mensagens a abrir junto com ?open
    tab: s.tab === "metricas" || s.tab === "marketing" || s.tab === "erros" ? s.tab : undefined,
  }),
});

const STATUS_LABEL: Record<string, string> = { active: "Ativo", created: "Aguardando", disabled: "Desativado" };
const STATUS_COLOR: Record<string, string> = { active: "var(--ok)", created: "var(--warn)", disabled: "var(--ink-muted)" };
const STATUS_BG: Record<string, string> = { active: "var(--ok-soft)", created: "var(--surface-input)", disabled: "var(--surface-card)" };
const STATUS_BORDER: Record<string, string> = { active: "var(--ok-line)", created: "var(--warn-line)", disabled: "var(--line-strong)" };

function ConnectionMiniCard({ conn, onClick }: { conn: any; onClick: () => void }) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered ? "var(--surface-card)" : "var(--surface-hover)",
        border: `1px solid ${hovered ? "var(--line-max)" : "var(--line)"}`,
        borderRadius: 12,
        padding: 16,
        cursor: "pointer",
        transition: "border-color 0.15s, background 0.15s",
        display: "flex",
        flexDirection: "column",
        gap: 10,
      }}
    >
      {/* Linha topo: tipo + status */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ fontSize: 10, fontWeight: 600, color: "var(--info)", background: "var(--info-soft)", border: "1px solid var(--info-line)", borderRadius: 4, padding: "2px 7px" }}>
          {conn.external ? "Externa" : "Cloud API"}
        </span>
        {conn.meta_disconnected_at ? (
          <span style={{
            fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 99,
            background: "var(--danger-soft)", color: "var(--danger)",
            border: "1px solid var(--danger)",
          }}>
            Desconectada
          </span>
        ) : (
          <span style={{
            fontSize: 10, padding: "2px 8px", borderRadius: 99,
            background: STATUS_BG[conn.status] ?? "var(--surface-input)",
            color: STATUS_COLOR[conn.status] ?? "var(--ink-soft)",
            border: `1px solid ${STATUS_BORDER[conn.status] ?? "var(--line-max)"}`,
          }}>
            {STATUS_LABEL[conn.status] ?? conn.status}
          </span>
        )}
      </div>

      {/* Nome da conexão */}
      <div>
        <div style={{ fontSize: 14, fontWeight: 600, color: "var(--ink)" }}>{conn.name}</div>
        {conn.phone_number && (
          <div style={{ fontSize: 12, color: "var(--ink-muted)", marginTop: 3, display: "flex", alignItems: "center", gap: 5 }}><PhoneIcon size={12} /> {conn.phone_number}</div>
        )}
      </div>

      {/* Rodapé: gestor + agente vinculado */}
      <div style={{ borderTop: "1px solid var(--line)", paddingTop: 8, display: "flex", flexDirection: "column", gap: 3 }}>
        {conn.owner_name && (
          <div style={{ fontSize: 11, color: "var(--ink-muted)" }}>
            <span style={{ color: "var(--line-max)" }}>Gestor: </span>
            {conn.owner_name}
            {(conn.participants?.length ?? 0) > 0 && (
              <span
                title={conn.participants.map((p: any) => p.user_name).join(", ")}
                style={{ marginLeft: 4, color: "var(--ink-secondary)", fontWeight: 600 }}
              >
                +{conn.participants.length}
              </span>
            )}
          </div>
        )}
        {conn.claimed_by_agent_id ? (
          <div style={{ fontSize: 11, color: "var(--ink-secondary)" }}>
            <span style={{ color: "var(--line-max)" }}>Agente: </span>
            {conn.claimed_by_agent_name ?? "(removido)"}
          </div>
        ) : (
          <div style={{ fontSize: 11, color: "var(--line-max)" }}>Sem agente vinculado</div>
        )}
      </div>
    </div>
  );
}

function WabaPage() {
  const qc = useQueryClient();
  const routeCtx = _authedRoute.useRouteContext() as { user?: CurrentUser };
  const currentUser = routeCtx.user;
  const isAdmin = currentUser?.role_name === "Admin";
  const canCreate = isAdmin || !!currentUser?.permissions?.waba_criar;
  const canDelete = isAdmin || !!currentUser?.permissions?.waba_excluir;
  const canManageParticipants = isAdmin || !!currentUser?.permissions?.waba_participantes;
  const canChangeWabaOwner = isAdmin || !!currentUser?.permissions?.waba_alterar_proprietario;
  const canCreateTemplate = isAdmin || !!currentUser?.permissions?.waba_criar_template;
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const createBusy = useRef(false);
  const [newExternal, setNewExternal] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterOwner, setFilterOwner] = useState("");
  const [selected, setSelected] = useState<any | null>(null);
  const [initialLog, setInitialLog] = useState<string | null>(null);
  const { open, log, tab } = Route.useSearch();
  const navigate = useNavigate();

  const { data: connections = [], isLoading, error } = useQuery({
    queryKey: ["waba-connections"],
    queryFn: () => wabaApi.get<any[]>("/connections"),
  });

  // Deep-link: ?open=<id> (vindo do agente) abre o modal de edição da conexão;
  // &log=<canal> abre junto o Log de Mensagens já filtrado (aba Portais → "Ver logs completos").
  useEffect(() => {
    if (!open) return;
    const c = connections.find((x: any) => x.id === open);
    if (c) {
      setInitialLog(log ?? null);
      setSelected(c);
      navigate({ to: "/waba", search: {}, replace: true });
    }
  }, [open, log, connections]);

  async function handleCreate() {
    // Ref (não state): dois Enter seguidos no mesmo render criavam 2 conexões.
    if (!newName.trim() || createBusy.current) return;
    createBusy.current = true;
    setCreateError(null);
    try {
      const conn = await wabaApi.post<any>("/connections", { name: newName.trim(), external: newExternal });
      setCreating(false);
      setNewName("");
      setNewExternal(false);
      await qc.invalidateQueries({ queryKey: ["waba-connections"] });
      setSelected(conn);
    } catch (err: any) {
      setCreateError(err?.payload?.detail ?? "Erro ao criar conexão.");
    } finally {
      createBusy.current = false;
    }
  }

  const ownerOptions = Array.from(new Set(connections.map((c: any) => c.owner_name).filter(Boolean))).sort() as string[];

  const q = search.toLowerCase();
  const filtered = connections
    .filter((c: any) => {
      const matchSearch = !q || c.name?.toLowerCase().includes(q) || c.phone_number?.includes(q) || c.claimed_by_agent_name?.toLowerCase().includes(q) || c.owner_name?.toLowerCase().includes(q);
      const matchStatus = !filterStatus || c.status === filterStatus;
      const matchOwner = !filterOwner || c.owner_name === filterOwner;
      return matchSearch && matchStatus && matchOwner;
    })
    // Desconectadas na Meta primeiro — são as que precisam de ação.
    .sort((a: any, b: any) => Number(!!b.meta_disconnected_at) - Number(!!a.meta_disconnected_at));

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <h1 style={{ position: "absolute", width: 1, height: 1, padding: 0, margin: -1, overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap", border: 0 }}>Conexões WABA</h1>

      {/* Modal nova conexão */}
      {creating && (
        <div
          style={{ position: "fixed", inset: 0, background: "var(--overlay)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: "var(--z-modal)" as any }}
          {...backdropClose(() => { setCreating(false); setNewName(""); setCreateError(null); })}
        >
          <DialogPanel onClose={() => { setCreating(false); setNewName(""); setCreateError(null); }} label="Nova conexão" style={{ background: "var(--surface-card)", border: "1px solid var(--line-bold)", borderRadius: 12, padding: 28, width: 400 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: "var(--ink)", marginBottom: 16 }}>Nova conexão</div>
            <input
              autoFocus
              style={{ width: "100%", padding: "9px 12px", background: "var(--surface-input)", border: "1px solid var(--line-bold)", borderRadius: 8, color: "var(--ink)", fontSize: 13, outline: "none", boxSizing: "border-box" }}
              placeholder="Nome da conexão (ex: CRMillion, Vendas)"
              value={newName}
              onChange={e => setNewName(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleCreate()}
            />
            <label style={{ display: "flex", alignItems: "flex-start", gap: 8, marginTop: 12, fontSize: 12, color: "var(--ink-secondary)", cursor: "pointer" }}>
              <input type="checkbox" checked={newExternal} onChange={e => setNewExternal(e.target.checked)} style={{ marginTop: 2 }} />
              <span>
                <strong style={{ color: "var(--ink)" }}>Conexão externa</strong> (sem WhatsApp oficial): o seu sistema entrega as
                mensagens em <code>POST /inbound</code> e recebe a resposta do agente num webhook. Pode migrar pra Meta depois.
              </span>
            </label>
            {createError && <span style={{ fontSize: 12, color: "var(--danger)", marginTop: 8, display: "block" }}>{createError}</span>}
            <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
              <button
                onClick={handleCreate}
                disabled={!newName.trim()}
                style={{ flex: 1, background: "var(--brand)", color: "#fff", border: "none", borderRadius: 8, padding: "9px 0", fontSize: 13, fontWeight: 600, cursor: "pointer", opacity: !newName.trim() ? 0.5 : 1 }}
              >
                Criar
              </button>
              <button
                onClick={() => { setCreating(false); setNewName(""); setCreateError(null); }}
                style={{ flex: 1, background: "transparent", color: "var(--ink-secondary)", border: "1px solid var(--line-bold)", borderRadius: 8, padding: "9px 0", fontSize: 13, cursor: "pointer" }}
              >
                Cancelar
              </button>
            </div>
          </DialogPanel>
        </div>
      )}

      {/* Modal de detalhes da conexão */}
      {selected && (
        <div
          style={{ position: "fixed", inset: 0, background: "var(--overlay)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: "var(--z-modal)" as any, padding: 24 }}
          {...backdropClose(() => { setSelected(null); })}
        >
          <DialogPanel onClose={() => setSelected(null)} label="Detalhes da conexão" style={{ maxHeight: "90vh", overflowY: "auto", borderRadius: 12 }}>
            <ConnectionCard
              conn={selected}
              initialLog={initialLog}
              canDelete={canDelete}
              canManageParticipants={canManageParticipants}
              canChangeOwner={canChangeWabaOwner}
              canCreateTemplate={canCreateTemplate}
              onChanged={async () => {
                await qc.invalidateQueries({ queryKey: ["waba-connections"] });
                const fresh = qc.getQueryData<any[]>(["waba-connections"]);
                const still = fresh?.find((c: any) => c.id === selected.id);
                if (!still) setSelected(null);
                else setSelected(still);
              }}
            />
          </DialogPanel>
          <button
            onClick={() => setSelected(null)}
            aria-label="Fechar"
            style={{ position: "fixed", top: 20, right: 24, background: "transparent", border: "none", color: "var(--ink-muted)", fontSize: 22, cursor: "pointer", zIndex: "calc(var(--z-modal) + 100)" as any }}
          >
            <XIcon size={20} />
          </button>
        </div>
      )}

      {/* Topbar */}
      <div style={{ height: 52, borderBottom: "1px solid var(--line)", display: "flex", alignItems: "center", padding: "0 24px", flexShrink: 0, gap: 10 }}>
        <span style={{ fontSize: 15, fontWeight: 600, color: "var(--ink)" }}>WABA</span>
        <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 4, background: "var(--surface-hover)", border: "1px solid var(--line)", color: "var(--ink-muted)" }}>
          WhatsApp Business API
        </span>
        <div style={{ flex: 1 }} />
        <a
          href="https://carmillion.mintlify.site"
          target="_blank"
          rel="noreferrer"
          title="Documentação da WABA API (guias + referência de endpoints)"
          style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "transparent", border: "1px solid var(--line-strong)", borderRadius: 8, padding: "6px 14px", fontSize: 13, color: "var(--ink-secondary)", textDecoration: "none", cursor: "pointer" }}
        >
          <BookOpenIcon size={14} /> Documentação
        </a>
        {canCreate && (
          <button
            onClick={() => setCreating(true)}
            style={{ background: "var(--brand)", color: "#fff", border: "none", borderRadius: 8, padding: "7px 16px", fontSize: 13, cursor: "pointer" }}
          >
            + Nova conexão
          </button>
        )}
      </div>

      {/* Abas */}
      <div style={{ display: "flex", borderBottom: "1px solid var(--line)", padding: "0 24px", flexShrink: 0 }}>
        {([["conexoes", "Conexões"], ["metricas", "Métricas"], ["marketing", "Marketing"], ...(isAdmin ? [["erros", "Erros"]] as const : [])] as const).map(([id, label]) => {
          const active = (tab ?? "conexoes") === id;
          return (
            <button
              key={id}
              onClick={() => navigate({ to: "/waba", search: id === "conexoes" ? {} : { tab: id }, replace: true })}
              style={{ padding: "10px 16px", fontSize: 13, background: "none", border: "none", borderBottom: `2px solid ${active ? "var(--brand)" : "transparent"}`, color: active ? "var(--ink)" : "var(--ink-muted)", cursor: "pointer", fontWeight: active ? 600 : 400 }}
            >
              {label}
            </button>
          );
        })}
      </div>

      {tab === "metricas" ? (
        <WabaMetricsTab canSeeAll={isAdmin || !!currentUser?.permissions?.waba_metricas_todas} />
      ) : tab === "marketing" ? (
        <WabaMarketingTab />
      ) : tab === "erros" && isAdmin ? (
        <WabaErrorsTab />
      ) : (
      <div style={{ flex: 1, overflow: "auto", padding: 24 }}>

        {/* Barra de busca */}
        <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap", maxWidth: 860 }}>
          <input
            style={{ flex: "1 1 220px", padding: "8px 12px", background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 8, color: "var(--ink)", fontSize: 13, outline: "none" }}
            placeholder="Buscar por nome, número ou agente..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <select
            style={{ padding: "8px 12px", background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 8, color: filterStatus ? "var(--ink)" : "var(--ink-muted)", fontSize: 13, outline: "none", cursor: "pointer" }}
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
          >
            <option value="">Todos os status</option>
            <option value="active">Ativo</option>
            <option value="created">Aguardando</option>
            <option value="disabled">Desativado</option>
          </select>
          {ownerOptions.length > 0 && (
            <select
              style={{ padding: "8px 12px", background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 8, color: filterOwner ? "var(--ink)" : "var(--ink-muted)", fontSize: 13, outline: "none", cursor: "pointer" }}
              value={filterOwner}
              onChange={e => setFilterOwner(e.target.value)}
            >
              <option value="">Todos os gestores</option>
              {ownerOptions.map(name => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          )}
        </div>



        {isLoading && <span style={{ fontSize: 13, color: "var(--ink-muted)" }}>Carregando conexões...</span>}
        {error && <span style={{ fontSize: 13, color: "var(--danger)" }}>Erro ao carregar. Verifique se o serviço WABA está online.</span>}

        {/* Grid de mini-cards */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 12 }}>
          {filtered.map((conn: any) => (
            <ConnectionMiniCard key={conn.id} conn={conn} onClick={() => setSelected(conn)} />
          ))}
        </div>

        {!isLoading && filtered.length === 0 && connections.length > 0 && (
          <span style={{ fontSize: 13, color: "var(--ink-muted)" }}>Nenhuma conexão encontrada para os filtros aplicados.</span>
        )}
        {!isLoading && connections.length === 0 && !creating && (
          <span style={{ fontSize: 13, color: "var(--ink-muted)" }}>Nenhuma conexão criada ainda. Clique em "+ Nova conexão" para começar.</span>
        )}
      </div>
      )}
    </div>
  );
}
